import React, { createContext, useContext, useState, useCallback } from 'react';

const CompareContext = createContext();

// Helper to parse car input string into structured data
const parseCarInput = (input, label) => {
  if (!input || input.trim() === '') return null;
  
  const trimmed = input.trim();
  
  // Check if it's a URL/link
  const isLink = trimmed.startsWith('http') || trimmed.includes('.com') || trimmed.includes('.co');
  
  // Try to extract year, make, model, mileage from text
  // Pattern: "2021 Honda Accord EX, 24000 miles" or similar
  const yearMatch = trimmed.match(/\b(19|20)\d{2}\b/);
  const mileageMatch = trimmed.match(/(\d{1,3}[,.]?\d{3})\s*(miles?|mi\.?|k\s*mi)/i);
  
  // Extract year
  const year = yearMatch ? yearMatch[0] : '';
  
  // Extract mileage
  let mileage = '';
  if (mileageMatch) {
    mileage = mileageMatch[1].replace(/[,.]/g, '');
  }
  
  // Try to extract make/model (rough heuristic)
  let makeModel = trimmed;
  if (year) {
    makeModel = makeModel.replace(year, '').trim();
  }
  if (mileageMatch) {
    makeModel = makeModel.replace(mileageMatch[0], '').trim();
  }
  // Clean up commas and extra spaces
  makeModel = makeModel.replace(/[,]/g, ' ').replace(/\s+/g, ' ').trim();
  
  return {
    label,
    raw: trimmed,
    year,
    makeModel: makeModel || 'Vehicle details',
    mileage,
    link: isLink ? trimmed : null,
    displayText: year && makeModel ? `${year} ${makeModel}${mileage ? `, ${parseInt(mileage).toLocaleString()} miles` : ''}` : trimmed
  };
};

export const CompareProvider = ({ children }) => {
  const [cars, setCars] = useState({
    car1: '',
    car2: '',
    car3: '',
    car4: '',
    car5: ''
  });

  const updateCar = (field, value) => {
    setCars(prev => ({ ...prev, [field]: value }));
  };

  const updateAllCars = (newCars) => {
    setCars(newCars);
  };

  // Get parsed car list (only non-empty cars)
  const getParsedCars = useCallback(() => {
    const labels = ['Car 1', 'Car 2', 'Car 3', 'Car 4', 'Car 5'];
    const fields = ['car1', 'car2', 'car3', 'car4', 'car5'];
    
    return fields
      .map((field, idx) => parseCarInput(cars[field], labels[idx]))
      .filter(car => car !== null);
  }, [cars]);

  return (
    <CompareContext.Provider value={{
      cars,
      setCars,
      updateCar,
      updateAllCars,
      getParsedCars
    }}>
      {children}
    </CompareContext.Provider>
  );
};

export const useCompare = () => {
  const context = useContext(CompareContext);
  if (!context) {
    throw new Error('useCompare must be used within CompareProvider');
  }
  return context;
};
